import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";

import Sidebar from "../../DesignSystem/Sidebar";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";

import { auth } from "../../../backend/firebaseConfig";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";

import { PARAPLANNING_ASSIGNED_TO_OPTIONS } from "../constants/paraplanningAssignedToOptions";
import { getParaplanningPermissionState } from "../utils/userPermissions";
import {
  formatParaplanningCurrency,
  normaliseParaplanningEmail,
} from "../utils/paraplanningBonusUtils";
import { useParaplannerSalaries } from "../hooks/useParaplannerSalaries";

import Breadcrumbs from "../../DesignSystem/Breadcrumb/Breadcrumb";

import "../../../css/Praplanning/ParaplanningSalaryUploadPage.css";

const roundMoney = (value = 0) => {
  return Math.round(Number(value || 0) * 100) / 100;
};

const getCurrentMonthKey = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");

  return `${today.getFullYear()}-${month}`;
};

const getAnnualSalaryFromMonthly = (monthlySalary = 0) => {
  return roundMoney(Number(monthlySalary || 0) * 12);
};

const getMonthlySalaryFromAnnual = (annualSalary = 0) => {
  return roundMoney(Number(annualSalary || 0) / 12);
};

const getMonthlySalaryFromRecord = (salaryRecord = {}) => {
  const monthlySalary = Number(salaryRecord?.monthlySalary || 0);

  if (monthlySalary) return monthlySalary;

  const storedAnnualSalary = Number(salaryRecord?.annualSalary || 0);

  if (!storedAnnualSalary) return 0;

  // Legacy fix: old records accidentally saved monthly salary in annualSalary.
  if (storedAnnualSalary < 10000) {
    return storedAnnualSalary;
  }

  return getMonthlySalaryFromAnnual(storedAnnualSalary);
};

const getInitialSalaryForm = (salaryRecord = {}) => {
  const monthlySalary = getMonthlySalaryFromRecord(salaryRecord);

  return {
    monthlySalary: monthlySalary ? String(monthlySalary) : "",
    effectiveFromMonth:
      salaryRecord?.effectiveFromMonth || getCurrentMonthKey(),
  };
};

const getHistoryMonthlySalary = (historyItem = {}) => {
  const monthlySalary = Number(historyItem?.monthlySalary || 0);

  if (monthlySalary) return monthlySalary;

  const storedAnnualSalary = Number(historyItem?.annualSalary || 0);

  if (!storedAnnualSalary) return 0;

  // Legacy fix: old history records may have monthly salary inside annualSalary.
  if (storedAnnualSalary < 10000) {
    return storedAnnualSalary;
  }

  return getMonthlySalaryFromAnnual(storedAnnualSalary);
};

const getHistoryAnnualSalary = (historyItem = {}) => {
  const annualSalary = Number(historyItem?.annualSalary || 0);

  if (annualSalary >= 10000) return annualSalary;

  return getAnnualSalaryFromMonthly(getHistoryMonthlySalary(historyItem));
};

const formatMonthKey = (monthKey = "") => {
  if (!monthKey) return "-";

  const [year, month] = String(monthKey).split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);

  if (Number.isNaN(date.getTime())) return monthKey;

  return date.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
};

const formatUpdatedDate = (value) => {
  if (!value) return "";

  const date = value?.toDate ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getSortedSalaryHistory = (salaryRecord = {}) => {
  const history = Array.isArray(salaryRecord?.salaryHistory)
    ? salaryRecord.salaryHistory
    : [];

  return [...history].sort((a, b) => {
    const aMonth = String(a?.effectiveFromMonth || "");
    const bMonth = String(b?.effectiveFromMonth || "");

    if (aMonth !== bMonth) {
      return bMonth.localeCompare(aMonth);
    }

    return Number(b?.createdAtMs || 0) - Number(a?.createdAtMs || 0);
  });
};

const ParaplanningSalaryUploadPage = () => {
  const { userPermissions = [] } = useUserPermissions();

  const { isAdmin } = getParaplanningPermissionState(userPermissions);

  const loggedInEmail =
    auth.currentUser?.email || localStorage.getItem("userEmail") || "";

  const { salaryMap, loadingSalaries, salariesError, saveSalary } =
    useParaplannerSalaries(isAdmin);

  const [salaryForms, setSalaryForms] = useState({});
  const [savingEmail, setSavingEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const paraplanners = useMemo(() => {
    return PARAPLANNING_ASSIGNED_TO_OPTIONS.filter((person) => person.email);
  }, []);

  const salaryLogRows = useMemo(() => {
    return paraplanners.map((person) => {
      const email = normaliseParaplanningEmail(person.email);
      const salaryRecord = salaryMap[email] || {};

      const monthlySalary = getMonthlySalaryFromRecord(salaryRecord);
      const annualSalary = getAnnualSalaryFromMonthly(monthlySalary);

      return {
        name: person.label || person.name || person.email,
        email,
        monthlySalary,
        annualSalary,
        calculation: monthlySalary
          ? `${monthlySalary} × 12 = ${annualSalary}`
          : "",
        effectiveFromMonth: salaryRecord?.effectiveFromMonth || "",
        historyCount: Array.isArray(salaryRecord?.salaryHistory)
          ? salaryRecord.salaryHistory.length
          : 0,
      };
    });
  }, [paraplanners, salaryMap]);

  useEffect(() => {
    if (!isAdmin) return;

    // console.groupCollapsed("[Paraplanner Salaries] Current salary records");
    // console.table(salaryLogRows);
    // console.groupEnd();
  }, [isAdmin, salaryLogRows]);

  const getFormForPerson = (person) => {
    const email = normaliseParaplanningEmail(person.email);
    const savedSalary = salaryMap[email];

    return salaryForms[email] || getInitialSalaryForm(savedSalary);
  };

  const updateFormValue = (email, field, value) => {
    const safeEmail = normaliseParaplanningEmail(email);

    setSalaryForms((prev) => ({
      ...prev,
      [safeEmail]: {
        ...getInitialSalaryForm(salaryMap[safeEmail]),
        ...(prev[safeEmail] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveSalary = async (person) => {
    const email = normaliseParaplanningEmail(person.email);
    const form = getFormForPerson(person);

    const monthlySalary = Number(form.monthlySalary || 0);
    const annualSalary = getAnnualSalaryFromMonthly(monthlySalary);

    setSavingEmail(email);
    setSuccessMessage("");
    setErrorMessage("");

    if (!monthlySalary || monthlySalary <= 0) {
      setSavingEmail("");
      setErrorMessage("Monthly salary must be greater than 0.");
      return;
    }

    try {
    //   console.log("[Paraplanner Salary Save]", {
    //     name: person.label || person.name || person.email,
    //     email,
    //     monthlySalary,
    //     annualSalary,
    //     calculation: `${monthlySalary} × 12 = ${annualSalary}`,
    //     effectiveFromMonth: form.effectiveFromMonth,
    //     updatedBy: loggedInEmail,
    //   });

      await saveSalary({
        email,
        name: person.label || person.name || person.email,
        monthlySalary,
        annualSalary,
        effectiveFromMonth: form.effectiveFromMonth,
        updatedBy: loggedInEmail,
      });

      setSalaryForms((prev) => {
        const next = { ...prev };
        delete next[email];
        return next;
      });

      setSuccessMessage("Salary updated successfully.");
    } catch (error) {
    //   console.error("[Paraplanning Salaries] Failed to save salary:", error);
      setErrorMessage(error.message || "Unable to save salary.");
    } finally {
      setSavingEmail("");
    }
  };

  if (!isAdmin) {
    return (
      <div className="main-content">
        <Helmet>
          <title>Paraplanner Salaries</title>
        </Helmet>

        <Sidebar />

        <div className="Main-Container ParaplanningSalaryPage">
          <h3 className="FP_H3">Paraplanner Salaries</h3>

          <div className="ParaplanningSalaryPage__AccessDenied FP_Content">
            You do not have permission to view this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <Helmet>
        <title>Paraplanner Salaries</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container ParaplanningSalaryPage">
            <Breadcrumbs
              items={[
                { label: "Finance", to: "/Department/Finance" },
                { label: "Paraplanner Salaries" },
              ]}
            />
        <div className="ParaplanningSalaryPage__Header">
          <div>

            <h3 className="FP_H3">Paraplanner Salaries</h3>

            <div className="FP_Help_Text">
              Upload or update each paraplanner’s monthly salary. The dashboard
              will calculate the annual salary automatically and use it for
              charge vs salary and bonus calculations.
            </div>
          </div>
        </div>

        {salariesError && (
          <div className="ParaplanningSalaryPage__Error FP_Content">
            {salariesError}
          </div>
        )}

        {errorMessage && (
          <div className="ParaplanningSalaryPage__Error FP_Content">
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div className="ParaplanningSalaryPage__Success FP_Content">
            {successMessage}
          </div>
        )}

        <div className="ParaplanningSalaryPage__Grid">
          {paraplanners.map((person) => {
            const email = normaliseParaplanningEmail(person.email);
            const savedSalary = salaryMap[email] || {};
            const salaryHistory = getSortedSalaryHistory(savedSalary);
            const form = getFormForPerson(person);

            const monthlySalary = getMonthlySalaryFromRecord(savedSalary);
            const annualSalary = getAnnualSalaryFromMonthly(monthlySalary);

            const formMonthlySalary = Number(form.monthlySalary || 0);
            const formAnnualSalary =
              getAnnualSalaryFromMonthly(formMonthlySalary);

            const isSaving = savingEmail === email;

            return (
              <div key={email} className="ParaplanningSalaryCard">
                <div className="ParaplanningSalaryCard__Header">
                  <div>
                    <div className="FP_Content_Semibold">
                      {person.label || person.name || person.email}
                    </div>

                    <div className="FP_Help_Text">{person.email}</div>
                  </div>
                </div>

                <div className="ParaplanningSalaryCard__Current">
                  <div>
                    <div className="FP_Help_Text">Current monthly salary</div>

                    <div className="FP_Subtitle_Semibold primary">
                      {monthlySalary
                        ? formatParaplanningCurrency(monthlySalary)
                        : "Not uploaded"}
                    </div>
                  </div>

                  <div>
                    <div className="FP_Help_Text">Current annual salary</div>

                    <div className="FP_Subtitle_Semibold primary">
                      {annualSalary
                        ? formatParaplanningCurrency(annualSalary)
                        : "Not uploaded"}
                    </div>
                  </div>

                  <div>
                    <div className="FP_Help_Text">Effective from</div>

                    <div className="FP_Content_Semibold">
                      {savedSalary?.effectiveFromMonth
                        ? formatMonthKey(savedSalary.effectiveFromMonth)
                        : "-"}
                    </div>
                  </div>
                </div>

                <div className="ParaplanningSalaryCard__Form">
                  <label className="ParaplanningSalaryCard__Field">
                    <span className="FP_Help_Text">Monthly salary</span>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.monthlySalary}
                      onChange={(event) =>
                        updateFormValue(
                          email,
                          "monthlySalary",
                          event.target.value,
                        )
                      }
                      placeholder="e.g. 2291.67"
                    />
                  </label>

                  <label className="ParaplanningSalaryCard__Field">
                    <span className="FP_Help_Text">Effective from</span>

                    <input
                      type="month"
                      value={form.effectiveFromMonth}
                      onChange={(event) =>
                        updateFormValue(
                          email,
                          "effectiveFromMonth",
                          event.target.value,
                        )
                      }
                    />
                  </label>

                  <div className="ParaplanningSalaryCard__Calculation FP_Help_Text">
                    Annual salary saved:{" "}
                    <strong>
                      {formAnnualSalary
                        ? formatParaplanningCurrency(formAnnualSalary)
                        : "-"}
                    </strong>
                    {formMonthlySalary ? ` (${formMonthlySalary} × 12)` : ""}
                  </div>
                </div>

                <div className="ParaplanningSalaryCard__Actions">
                  <SecondaryButton
                    type="button"
                    label={isSaving ? "Saving..." : "Save Salary"}
                    disabled={loadingSalaries || isSaving}
                    onClick={() => handleSaveSalary(person)}
                  />
                </div>

                <div className="ParaplanningSalaryCard__History">
                  <div className="ParaplanningSalaryCard__HistoryHeader">
                    <div className="FP_Content_Semibold">Salary history</div>

                    <div className="FP_Help_Text">
                      {salaryHistory.length
                        ? `${salaryHistory.length} update${
                            salaryHistory.length === 1 ? "" : "s"
                          }`
                        : "No previous history"}
                    </div>
                  </div>

                  {salaryHistory.length > 0 ? (
                    <div className="ParaplanningSalaryCard__HistoryList">
                      {salaryHistory.map((historyItem, index) => {
                        const historyMonthlySalary =
                          getHistoryMonthlySalary(historyItem);
                        const historyAnnualSalary =
                          getHistoryAnnualSalary(historyItem);

                        return (
                          <div
                            key={
                              historyItem.id ||
                              `${email}-${historyItem.effectiveFromMonth}-${index}`
                            }
                            className="ParaplanningSalaryCard__HistoryItem"
                          >
                            <div>
                              <div className="FP_Content_Semibold">
                                {formatParaplanningCurrency(
                                  historyMonthlySalary,
                                )}{" "}
                                per month
                              </div>

                              <div className="FP_Help_Text">
                                {formatParaplanningCurrency(
                                  historyAnnualSalary,
                                )}{" "}
                                annually
                              </div>

                              <div className="FP_Help_Text">
                                Effective from{" "}
                                {formatMonthKey(historyItem.effectiveFromMonth)}
                              </div>
                            </div>

                            <div className="ParaplanningSalaryCard__HistoryMeta">
                              {(historyItem.updatedBy ||
                                historyItem.createdAtMs ||
                                historyItem.createdAt) && (
                                <div className="FP_Help_Text">
                                  Updated
                                  {historyItem.updatedBy
                                    ? ` by ${historyItem.updatedBy}`
                                    : ""}
                                  {historyItem.createdAtMs ||
                                  historyItem.createdAt
                                    ? ` on ${formatUpdatedDate(
                                        historyItem.createdAtMs ||
                                          historyItem.createdAt,
                                      )}`
                                    : ""}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="ParaplanningSalaryCard__EmptyHistory FP_Help_Text">
                      Once this salary is saved, each future change will appear
                      here.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ParaplanningSalaryUploadPage;
